<script lang="ts">
  import { gmailState } from "../gmail/store.svelte";
  import { loadInbox } from "../gmail/sync";
  import { searchContacts, type Contact } from "../gmail/contacts.svelte";
  import { labelsState, displayLabel } from "../gmail/labels.svelte";
  import {
    FACET_GROUPS,
    applyFacet,
    hasToken,
    tokenize,
    removeToken,
  } from "../gmail/searchFacets";
  import { get, set } from "idb-keyval";
  import Icon from "./shared/Icon.svelte";
  import { haptic } from "../gestures/haptic";

  interface Props {
    visible: boolean;
    onClose: () => void;
  }

  let { visible, onClose }: Props = $props();

  let value = $state("");
  let inputEl: HTMLInputElement | undefined = $state();
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Auto-focus à l'ouverture
  $effect(() => {
    if (visible && inputEl) {
      setTimeout(() => inputEl?.focus(), 50);
    }
  });

  /**
   * Détecte un partial `from:|to:|cc:<partial>` à la fin du texte tapé.
   * Renvoie l'info nécessaire pour montrer + appliquer la suggestion.
   * Le regex match du DERNIER token car l'user pourrait avoir tapé
   * `subject:offre from:c` — on suggère sur `c`, pas sur `o`.
   */
  interface SuggestionContext {
    /** Position dans `value` où commence le préfixe (ex: index de "f" dans "from:c"). */
    tokenStart: number;
    /** Le préfixe tel que tapé (`from`, `to`, `cc`). */
    prefix: string;
    /** Ce qui a été tapé après le `:` (peut être ""). */
    partial: string;
    list: Contact[];
  }

  const suggestions = $derived.by<SuggestionContext | null>(() => {
    if (!visible) return null;
    const m = value.match(/(^|\s)(from|to|cc):([^\s]*)$/i);
    if (!m) return null;
    const partial = m[3];
    if (partial.length < 1) return null;
    const list = searchContacts(partial, 6);
    if (list.length === 0) return null;
    return {
      tokenStart: (m.index ?? 0) + m[1].length,
      prefix: m[2].toLowerCase(),
      partial,
      list,
    };
  });

  function commitSearch(q: string) {
    const trimmed = q.trim();
    gmailState.filter = {
      ...gmailState.filter,
      query: trimmed.length > 0 ? trimmed : null,
    };
    void loadInbox();
  }

  function onInput() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => commitSearch(value), 350);
  }

  function onSubmit(e: Event) {
    e.preventDefault();
    if (debounceTimer) clearTimeout(debounceTimer);
    commitSearch(value);
    void remember(value);
    inputEl?.blur();
  }

  function onClear() {
    value = "";
    if (debounceTimer) clearTimeout(debounceTimer);
    commitSearch("");
    inputEl?.focus();
  }

  function onCancel() {
    if (debounceTimer) clearTimeout(debounceTimer);
    value = "";
    commitSearch("");
    onClose();
  }

  // === Jetons ============================================================
  // Dérivés de la chaîne, jamais l'inverse : taper `from:` à la main reste
  // le chemin le plus court, et les facettes doivent s'y plier.
  const tokens = $derived(tokenize(value));

  function dropToken(t: string) {
    haptic("light");
    value = removeToken(value, t);
    commitSearch(value);
  }

  function toggleFacet(groupIndex: number, facetIndex: number) {
    haptic("light");
    const g = FACET_GROUPS[groupIndex];
    value = applyFacet(value, g, g.facets[facetIndex]);
    commitSearch(value);
  }

  function toggleLabel(name: string) {
    haptic("light");
    const tok = `label:${name.includes(" ") ? `"${name}"` : name}`;
    value = hasToken(value, tok) ? removeToken(value, tok) : `${value.trim()} ${tok}`.trim();
    commitSearch(value);
  }

  // === Recherches récentes ================================================
  const RECENTS_KEY = "mailfox:search-recents";
  let recents = $state<string[]>([]);

  $effect(() => {
    if (!visible) return;
    void (async () => {
      try {
        recents = (await get<string[]>(RECENTS_KEY)) ?? [];
      } catch {
        recents = [];
      }
    })();
  });

  async function remember(q: string) {
    const t = q.trim();
    if (t.length < 3) return;
    const next = [t, ...recents.filter((r) => r !== t)].slice(0, 8);
    recents = next;
    try {
      await set(RECENTS_KEY, next);
    } catch {
      // Sans persistance la liste vit le temps de la session, ça suffit.
    }
  }

  function applySuggestion(c: Contact) {
    const ctx = suggestions;
    if (!ctx) return;
    const before = value.slice(0, ctx.tokenStart);
    // Remplace le token entier (`from:c`) par le complet (`from:<email> `)
    value = `${before}${ctx.prefix}:${c.email} `;
    if (debounceTimer) clearTimeout(debounceTimer);
    commitSearch(value);
    inputEl?.focus();
  }
</script>

{#if visible}
  <div class="screen">
    <form class="bar" onsubmit={onSubmit}>
      <div class="field">
        <Icon name="search" size={15} />
        <input
          bind:this={inputEl}
          bind:value
          oninput={onInput}
          type="search"
          autocomplete="off"
          autocapitalize="off"
          spellcheck="false"
          placeholder="from:nom  has:attachment  newer_than:7d"
          aria-label="Rechercher"
        />
        {#if value}
          <button type="button" class="clear" onclick={onClear} aria-label="Effacer">
            <Icon name="close" size={12} />
          </button>
        {/if}
      </div>
      <button type="button" class="cancel" onclick={onCancel}>Annuler</button>
    </form>

    {#if tokens.length > 0}
      <div class="tokens no-scrollbar">
        {#each tokens as t (t)}
          <button class="token" onclick={() => dropToken(t)}>
            {t}<Icon name="close" size={10} />
          </button>
        {/each}
      </div>
    {/if}

    {#if suggestions}
      <ul class="suggest">
        {#each suggestions.list as c (c.email)}
          <li>
            <button onclick={() => applySuggestion(c)}>
              <span class="s-name">{c.name || c.email}</span>
              <span class="s-mail">{c.email}</span>
            </button>
          </li>
        {/each}
      </ul>
    {:else}
      <div class="facets">
        {#each FACET_GROUPS as g, gi (g.title)}
          <section>
            <h3>{g.title}</h3>
            <div class="chips">
              {#each g.facets as f, fi (f.label)}
                <button
                  class="chip"
                  class:on={f.op ? hasToken(value, f.op) : false}
                  onclick={() => toggleFacet(gi, fi)}
                >{f.label}</button>
              {/each}
            </div>
          </section>
        {/each}

        {#if labelsState.splits.length > 0}
          <section>
            <h3>Label</h3>
            <div class="chips">
              {#each labelsState.splits as l (l.id)}
                {@const name = displayLabel(l.name)}
                <button
                  class="chip"
                  class:on={hasToken(value, `label:${name.includes(" ") ? `"${name}"` : name}`)}
                  onclick={() => toggleLabel(name)}
                >{name}</button>
              {/each}
            </div>
          </section>
        {/if}

        {#if recents.length > 0}
          <section>
            <h3>Recherches récentes</h3>
            <ul class="recents">
              {#each recents as r (r)}
                <li>
                  <button onclick={() => { value = r; commitSearch(r); }}>
                    <Icon name="clock" size={13} />
                    <span>{r}</span>
                  </button>
                </li>
              {/each}
            </ul>
          </section>
        {/if}
      </div>
    {/if}
  </div>
{/if}

<style>
  /* Écran plein, au-dessus de tout : la recherche n'est pas un bandeau
     posé sur la liste, c'est une destination. */
  .screen {
    position: fixed;
    inset: 0;
    z-index: 45;
    overflow-y: auto;
    background: var(--bg);
    padding-top: env(safe-area-inset-top);
    padding-bottom: calc(env(safe-area-inset-bottom) + 24px);
  }

  .bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--border-subtle);
  }
  .field {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 11px;
    border: 1px solid var(--border);
    background: var(--bg-elev);
    border-radius: var(--radius-ctl);
    color: var(--fg-subtle);
  }
  .field input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: none;
    /* La syntaxe des opérateurs se lit mieux en chasse fixe. */
    font-family: var(--font-mono);
    font-size: max(0.92rem, 16px);
    color: var(--fg);
  }
  .field input::-webkit-search-cancel-button { display: none; }
  .clear {
    display: grid;
    place-items: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--bg-active);
    color: var(--fg-muted);
  }
  .cancel {
    flex-shrink: 0;
    font-size: 13.5px;
    font-weight: 500;
    color: var(--accent);
  }

  .tokens {
    display: flex;
    gap: 6px;
    padding: 11px 16px;
    overflow-x: auto;
    border-bottom: 1px solid var(--border-subtle);
  }
  .token {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 27px;
    padding: 0 10px;
    border-radius: var(--radius-pill);
    background: var(--accent-soft);
    color: var(--accent);
    font-family: var(--font-mono);
    font-size: 11px;
  }

  .facets section {
    padding: 14px 16px 12px;
    border-bottom: 1px solid var(--border-subtle);
  }
  h3 {
    margin: 0 0 9px;
    font-family: var(--font-mono);
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .chip {
    height: 30px;
    padding: 0 12px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-btn);
    font-size: 12px;
    font-weight: 500;
    color: var(--fg-muted);
    transition: var(--tr);
  }
  .chip.on {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--accent);
  }

  .recents { list-style: none; margin: 0; padding: 0; }
  .recents button {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-subtle);
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--fg-muted);
    text-align: left;
  }
  .recents span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .suggest { list-style: none; margin: 0; padding: 0; }
  .suggest button {
    display: flex;
    flex-direction: column;
    gap: 1px;
    width: 100%;
    padding: 10px 16px;
    border-bottom: 1px solid var(--border-subtle);
    text-align: left;
  }
  .s-name { font-size: 14px; font-weight: 500; color: var(--fg); }
  .s-mail {
    font-family: var(--font-mono);
    font-size: 11.5px;
    color: var(--fg-subtle);
  }
</style>
